import React from 'react';
import PageHero from './PageHero';
import { Target, Heart, ShieldCheck, Zap, HandHeart, Quote, Play } from 'lucide-react';
import heroImg from '../assets/WHY GMIS - HOLISTIC.png'; // Using an existing image

// Maps the 5 values to visually distinct icons
const getIcon = (index) => {
  const icons = [
    <ShieldCheck size={28} className="text-navy" strokeWidth={1.7} />,
    <Heart size={28} className="text-navy" strokeWidth={1.7} />,
    <Target size={28} className="text-navy" strokeWidth={1.7} />,
    <Zap size={28} className="text-navy" strokeWidth={1.7} />,
    <HandHeart size={28} className="text-navy" strokeWidth={1.7} />
  ];
  return icons[index] || icons[0];
};

export default function GandhianValuesPage({ setPage, t }) {
  const { gandhianValues: gv } = t;

  // Split title "Satya — Truth & Integrity" into Sanskrit and English
  const formatTitle = (titleStr) => {
    const parts = titleStr.split('—');
    if (parts.length === 2) {
      return { sanskrit: parts[0].trim(), english: parts[1].trim() };
    }
    return { sanskrit: titleStr, english: '' };
  };

  const practiceCards = [
    {
      title: 'In the Classroom',
      desc: 'Teachers model honesty and accountability, encouraging students to ask questions, admit mistakes and own their learning.',
      img: 'https://images.unsplash.com/photo-1588075592446-265fd1e6e76f?auto=format&fit=crop&w=700&q=80'
    },
    {
      title: 'On Campus',
      desc: 'Daily routines, from assemblies to peer mentoring, reinforce respect, discipline and care for the GMIS community.',
      img: 'https://images.unsplash.com/photo-1639548538099-6f7f9aec3b92?auto=format&fit=crop&w=700&q=80'
    },
    {
      title: 'In the Community',
      desc: 'Service projects and outreach give students real opportunities to put Seva into practice beyond the classroom.',
      img: 'https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?auto=format&fit=crop&w=700&q=80'
    }
  ];

  return (
    <div className="w-full bg-white flex flex-col font-sans text-ink">
      
            {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero title={gv.title} breadcrumbs={["Home","About Us","Gandhian Values"]} subtitle={gv.subtitle} />


      {/* ── QUOTE BAND ── */}
      <div className="h-auto lg:h-[180px] shrink-0 box-border px-6 py-12 lg:px-[120px] lg:py-0 flex items-center justify-center bg-paper border-y border-line">
        <div className="max-w-[920px] flex flex-col items-center gap-3 text-center">
          <Quote className="text-gold w-[30px] h-[30px]" strokeWidth={1.6} />
          <div className="font-heading font-semibold text-[20px] lg:text-[25px] leading-[1.5] text-navy italic tracking-[-0.005em]">
            “Be the change you wish to see in the world.”
          </div>
          <div className="text-[12.5px] font-bold tracking-[0.12em] uppercase text-muted">
            Mahatma Gandhi
          </div>
        </div>
      </div>

      {/* ── VALUES LIST ── */}
      <div className="shrink-0 box-border px-6 py-16 lg:px-[120px] lg:pt-[88px] lg:pb-[20px]">
        <div className="flex flex-col gap-3 max-w-[760px] mb-12 lg:mb-[56px]">
          <div className="flex items-center gap-3 text-xs font-bold tracking-[0.18em] uppercase text-gold-ink">
            <span className="w-8 h-[2px] bg-gold"></span>Our Foundation
          </div>
          <h2 className="m-0 font-heading font-bold text-[32px] lg:text-[42px] leading-[1.1] text-navy tracking-[-0.01em]">
            Five Values, One Shared Purpose
          </h2>
          <p className="m-0 text-[16px] leading-[1.7] text-muted">
            Every programme, lesson and tradition at GMIS is built on these five principles.
          </p>
        </div>

        <div className="flex flex-col border-t border-line">
          {gv.values.map((v, i) => {
            const { sanskrit, english } = formatTitle(v.title);
            return (
              <div 
                key={i} 
                className="group flex flex-col md:flex-row items-start gap-4 md:gap-[40px] p-6 lg:p-[38px_16px] border-bottom border-line hover:bg-paper transition-colors duration-150 border-b last:border-b-0"
              >
                <div className="w-[56px] shrink-0 pt-1 font-heading font-bold text-[22px] text-gold hidden md:block">
                  0{i + 1}
                </div>
                <div className="w-[68px] h-[68px] shrink-0 rounded-[14px] border-[1.5px] border-line bg-paper flex items-center justify-center text-navy group-hover:bg-white transition-colors duration-150">
                  {getIcon(i)}
                </div>
                <div className="flex-1 flex flex-col gap-2 pt-1">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-heading font-semibold text-[24px] lg:text-[27px] text-navy tracking-[-0.005em]">
                      {sanskrit}
                    </span>
                    {english && (
                      <span className="text-[12.5px] font-bold tracking-[0.1em] uppercase text-gold-ink">
                        {english}
                      </span>
                    )}
                  </div>
                  <p className="m-0 max-w-[620px] text-[15.5px] leading-[1.75] text-muted">
                    {v.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── IN PRACTICE ── */}
      <div className="shrink-0 box-border px-6 py-16 lg:px-[120px] lg:pt-[60px] lg:pb-[96px] bg-paper">
        <div className="flex flex-col gap-3 max-w-[760px] mb-10 lg:mb-[44px]">
          <div className="flex items-center gap-3 text-xs font-bold tracking-[0.18em] uppercase text-gold-ink">
            <span className="w-8 h-[2px] bg-gold"></span>Beyond the Classroom
          </div>
          <h2 className="m-0 font-heading font-bold text-[28px] lg:text-[36px] leading-[1.15] text-navy tracking-[-0.01em]">
            Values in Everyday Life at GMIS
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {practiceCards.map((p, i) => (
            <div key={i} className="bg-white border border-line rounded-[10px] overflow-hidden flex flex-col hover:shadow-lg transition-shadow duration-300">
              <div className="h-[160px] relative overflow-hidden bg-[#cddbe6]">
                <img src={p.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover block hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6 flex flex-col gap-2.5">
                <span className="w-[28px] h-[3px] bg-gold"></span>
                <h3 className="m-0 font-heading font-bold text-[19px] text-ink">
                  {p.title}
                </h3>
                <p className="m-0 text-[14px] leading-[1.7] text-muted">
                  {p.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="h-auto lg:h-[320px] shrink-0 box-border px-6 py-16 lg:px-[120px] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10 lg:gap-16 bg-navy text-white">
        <div className="flex flex-col gap-4 max-w-[800px]">
          <span className="w-[48px] h-[3px] bg-gold"></span>
          <h2 className="m-0 font-heading font-semibold text-[36px] lg:text-[48px] leading-[1.1] tracking-[-0.01em] text-balance">
            See These Values in Action
          </h2>
          <p className="m-0 text-[18px] text-white/90">
            Visit our campus and meet the community that lives by these principles every day.
          </p>
        </div>
        <button 
          onClick={() => { if (setPage) { setPage('contact'); window.scrollTo(0, 0); } }}
          className="shrink-0 flex items-center gap-3 h-[56px] px-8 rounded-[3px] bg-gold text-[var(--navy-deep)] text-[13px] font-bold tracking-[0.1em] uppercase hover:brightness-110 transition-all"
        >
          Enquire Now <Play size={16} fill="currentColor" />
        </button>
      </div>

    </div>
  );
}
